import { api } from './api';
import { 
  Diagnosis, 
  DiagnosisData, 
  ICD10Option,
  DiagnosisStatistics 
} from '@/types/diagnosis.types';
import { CHDCode } from './chdCodeService';
import { PaginatedResponse } from '@/types/api.types';

// MCC subgroup interface
export interface CHDSubgroup {
  name: string;
  count: number;
}

// Diagnosis service with Romanian error messages
export const diagnosisService = {
  
  // Get diagnoses by patient ID
  getDiagnosesByPatient: async (patientId: number): Promise<Diagnosis[]> => {
    if (!patientId || patientId <= 0) {
      throw new Error('ID-ul pacientului este invalid');
    }
    
    const response = await api.get<any>(`/api/diagnoses/patient/${patientId}`);
    
    if (response.success && response.data) {
      let diagnoses: Diagnosis[] = [];
      
      // Ensure the response data is an array
      if (Array.isArray(response.data)) {
        diagnoses = response.data;
      } else if (response.data.diagnoses && Array.isArray(response.data.diagnoses)) {
        diagnoses = response.data.diagnoses;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        diagnoses = response.data.data;
      } else {
        console.warn('Patient diagnoses response is not an array:', response.data);
        return [];
      }
      
      // Convert is_primary from integer to boolean
      return diagnoses.map(diagnosis => ({
        ...diagnosis,
        is_primary: Boolean(diagnosis.is_primary)
      }));
    }
    
    // Return empty array instead of throwing error when no data
    return [];
  },

  // Get all diagnoses with pagination
  getAllDiagnoses: async (): Promise<Diagnosis[]> => {
    const response = await api.get<any>('/api/diagnoses');
    
    if (response.success && response.data) {
      let diagnoses: Diagnosis[] = [];
      
      // Ensure the response data is an array
      if (Array.isArray(response.data)) {
        diagnoses = response.data;
      } else if (response.data.diagnoses && Array.isArray(response.data.diagnoses)) {
        diagnoses = response.data.diagnoses;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        diagnoses = response.data.data;
      } else {
        console.warn('Diagnoses response is not an array:', response.data);
        return [];
      }
      
      // Convert is_primary from integer to boolean and map description fields
      return diagnoses.map(diagnosis => ({
        ...diagnosis,
        is_primary: Boolean(diagnosis.is_primary),
        // Map backend description fields to diagnosis_name for compatibility
        diagnosis_name_ro: diagnosis.diagnosis_name_ro || diagnosis.description_ro,
        diagnosis_name_en: diagnosis.diagnosis_name_en || diagnosis.description_en
      }));
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca diagnosticele');
  },

  // Create new diagnosis
  createDiagnosis: async (data: DiagnosisData): Promise<Diagnosis> => {
    // Validate required fields
    if (!data.patient_id || data.patient_id <= 0) {
      throw new Error('ID-ul pacientului este obligatoriu');
    }
    
    if (!data.icd10_code?.trim()) {
      throw new Error('Codul ICD-10 este obligatoriu');
    }
    
    if (!data.diagnosis_date) {
      throw new Error('Data diagnosticului este obligatorie');
    }
    
    if (data.severity_level < 1 || data.severity_level > 5) {
      throw new Error('Nivelul de severitate trebuie să fie între 1 și 5');
    }

    const response = await api.post<any>('/api/diagnoses', {
      patient_id: data.patient_id,
      icd10_code: data.icd10_code.trim(),
      is_primary: Boolean(data.is_primary),
      severity_level: data.severity_level,
      diagnosis_date: data.diagnosis_date,
      notes: data.notes?.trim() || null
    });
    
    if (response.success && response.data) {
      // Backend returns { message: string, diagnosis: Diagnosis }
      const diagnosis = response.data.diagnosis || response.data;
      // Convert is_primary from integer to boolean
      return {
        ...diagnosis,
        is_primary: Boolean(diagnosis.is_primary)
      };
    }
    
    throw new Error(response.error || 'Nu s-a putut crea diagnosticul');
  },

  // Update existing diagnosis
  updateDiagnosis: async (id: number, data: DiagnosisData): Promise<Diagnosis> => {
    if (!id || id <= 0) {
      throw new Error('ID-ul diagnosticului este invalid');
    }
    
    // Validate required fields
    if (!data.patient_id || data.patient_id <= 0) {
      throw new Error('ID-ul pacientului este obligatoriu');
    }
    
    if (!data.icd10_code?.trim()) {
      throw new Error('Codul ICD-10 este obligatoriu');
    }
    
    if (!data.diagnosis_date) {
      throw new Error('Data diagnosticului este obligatorie');
    }
    
    if (data.severity_level < 1 || data.severity_level > 5) {
      throw new Error('Nivelul de severitate trebuie să fie între 1 și 5');
    }

    const response = await api.put<any>(`/api/diagnoses/${id}`, {
      patient_id: data.patient_id,
      icd10_code: data.icd10_code.trim(),
      is_primary: Boolean(data.is_primary),
      severity_level: data.severity_level,
      diagnosis_date: data.diagnosis_date,
      notes: data.notes?.trim() || null
    });
    
    if (response.success && response.data) {
      // Backend returns { message: string, diagnosis: Diagnosis }
      const diagnosis = response.data.diagnosis || response.data;
      // Convert is_primary from integer to boolean
      return {
        ...diagnosis,
        is_primary: Boolean(diagnosis.is_primary)
      };
    }
    
    throw new Error(response.error || 'Nu s-a putut actualiza diagnosticul');
  },

  // Delete diagnosis
  deleteDiagnosis: async (id: number): Promise<void> => {
    if (!id || id <= 0) {
      throw new Error('ID-ul diagnosticului este invalid');
    }
    
    const response = await api.delete<any>(`/api/diagnoses/${id}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Nu s-a putut șterge diagnosticul');
    }
  },

  // Set diagnosis as primary
  setPrimaryDiagnosis: async (id: number): Promise<Diagnosis> => {
    if (!id || id <= 0) {
      throw new Error('ID-ul diagnosticului este invalid');
    }
    
    const response = await api.put<any>(`/api/diagnoses/${id}/primary`);
    
    if (response.success && response.data) {
      // Backend returns { message: string, diagnosis: Diagnosis }
      return response.data.diagnosis || response.data;
    }
    
    throw new Error(response.error || 'Nu s-a putut seta diagnosticul ca principal');
  },

  // Get active ICD-10 codes for MCC
  getICD10Codes: async (): Promise<ICD10Option[]> => {
    const response = await api.get<any>('/api/chd-codes/active');
    
    if (response.success && response.data) {
      // The API returns data wrapped in 'chd_codes' property
      const codes = response.data.chd_codes || response.data;
      
      // Ensure codes is an array before mapping
      if (Array.isArray(codes)) {
        return codes.map((code: CHDCode) => ({
          code: code.icd10_code,
          description: code.description_ro,
          eurocat_subgroup: code.eurocat_subgroup || undefined
        }));
      }
      
      // If not an array, return empty array
      console.warn('ICD10 codes response is not an array:', response.data);
      return [];
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca codurile ICD-10');
  },

  // Get MCC subgroups statistics
  getCHDSubgroups: async (): Promise<CHDSubgroup[]> => {
    const response = await api.get<any>('/api/chd-codes/subgroups');
    
    if (response.success && response.data) {
      // The API returns data wrapped in 'subgroups' property
      const subgroups = response.data.subgroups || response.data;
      
      // Ensure subgroups is an array
      if (Array.isArray(subgroups)) {
        return subgroups;
      }
      
      // If not an array, return empty array
      console.warn('MCC subgroups response is not an array:', response.data);
      return [];
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca subgrupurile MCC');
  },

  // Get diagnosis statistics
  getDiagnosisStatistics: async (): Promise<DiagnosisStatistics> => {
    const response = await api.get<any>('/api/diagnoses/statistics');
    
    if (response.success && response.data) {
      // Backend returns statistics wrapped in 'statistics' property
      const stats = response.data.statistics || response.data;
      
      // Map all backend fields to frontend interface
      return {
        total_diagnoses: parseInt(stats.total_diagnoses) || 0,
        patients_with_diagnoses: parseInt(stats.patients_with_diagnoses) || 0,
        primary_diagnoses: parseInt(stats.primary_diagnoses) || 0,
        severe_chd_diagnoses: parseInt(stats.severe_chd_diagnoses) || 0,
        unique_diagnoses: parseInt(stats.unique_diagnoses) || 0,
        hospitals_with_diagnoses: parseInt(stats.hospitals_with_diagnoses) || 0,
        avg_severity: parseFloat(stats.avg_severity) || 0,
        // These fields are not provided by current backend but kept for future use
        by_severity: stats.by_severity || {},
        by_icd10_category: stats.by_icd10_category || [],
        primary_vs_secondary: {
          primary: parseInt(stats.primary_diagnoses) || 0,
          secondary: (parseInt(stats.total_diagnoses) || 0) - (parseInt(stats.primary_diagnoses) || 0)
        }
      };
    }
    
    throw new Error(response.error || 'Nu s-au putut încărca statisticile diagnosticelor');
  },

  // Helper method to format diagnosis display
  formatDiagnosis: (diagnosis: Diagnosis): string => {
    const name = diagnosis.diagnosis_name_ro || diagnosis.diagnosis_name_en || '';
    return name ? `${diagnosis.icd10_code} - ${name}` : diagnosis.icd10_code;
  },

  // Helper method to get severity level label in Romanian
  getSeverityLabel: (level: number): string => {
    const labels = {
      1: 'Ușor',
      2: 'Moderat',
      3: 'Sever',
      4: 'Critic',
      5: 'Foarte critic'
    };
    return labels[level as keyof typeof labels] || 'Necunoscut';
  },

  // Helper method to validate ICD-10 code format
  validateICD10Code: (code: string): boolean => {
    const icd10Pattern = /^[A-Z]\d{2}(\.\d)?$/;
    return icd10Pattern.test(code.trim().toUpperCase());
  },

  // Helper method to format diagnosis date for display
  formatDiagnosisDate: (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }
};